'use client';

import { motion } from 'framer-motion';
import {
    Upload, ArrowRight, Brain, Calculator, FileJson, Database, Shield, Lock,
    CheckCircle, BarChart3, Clock, Zap, BookOpen, Scale, Globe, Layers,
    Server, Eye, Key, Fingerprint, Bot, Cpu, TrendingUp, FileText, Sun, Moon, Landmark} from 'lucide-react';
import Link from 'next/link';
import { ThemeProvider, useTheme } from '@/components/ThemeContext';

/* ═══════════════════ DATA ═══════════════════════ */
const PIPELINE_STEPS = [
    { icon: Upload, label: '설계 모델 업로드', desc: 'BIM, EDX 포맷의 설계 모델을 CodexHub에 업로드', color: '#3b82f6' },
    { icon: Brain, label: 'CodexHub', desc: 'AI 기반 설계 파일 관리 및 분석 플랫폼', color: '#22c55e' },
    { icon: Calculator, label: 'SnapPrice', desc: 'AI 기반 비용 계산 엔진', color: '#a855f7' },
    { icon: FileJson, label: 'CXD 생성', desc: 'Completed/Certified eXchange Data (JSON 형식)', color: '#f59e0b' },
];

const CODEXHUB_FEATURES = [
    'IFC 등 구조화된 BIM 스키마를 JSON으로 변환',
    '건축 요소, 수량, 속성 등 자동 추출',
    'AI 기반 설계도면 분석 및 정리',
    '디지털 서명과 암호화로 데이터 무결성 보장',
];

const SNAPPRICE_FEATURES = [
    '구조화 데이터 기반 공사비 자동 산출',
    '단가, 공수 등 정보 자동 적용',
    '머신러닝 모델로 비용 패턴 학습',
    '실시간 시장 단가 반영 및 업데이트',
];

const CXD_COMPONENTS = [
    '건축 자재 목록(BOM) 정보',
    '부위별 공사비 상세 내역',
    'ML 학습을 거친 구조화 JSON 데이터',
    '로봇·자동화 시스템 명령어로 활용 가능',
];

const CXD_JSON = `{
  "cxd_version": "1.0",
  "project_info": {
    "name": "오피스 빌딩 A",
    "client": "ABC 건설",
    "created_date": "2025-04-15"
  },
  "building_materials": [
    {
      "id": "M001",
      "type": "콘크리트",
      "spec": "고강도, 25MPa",
      "quantity": 1200,
      "unit": "m³"
    }
  ],
  "cost_summary": {
    "total": 15200000000,
    "currency": "KRW"
  }
}`;

const CXD_DETAIL_ITEMS = [
    { icon: Layers, title: '건축 자재 목록 (BOM)', desc: '설계에 필요한 모든 자재, 부품, 장비 등의 정량적 정보와 속성이 포함됩니다. 자재 타입, 규격, 수량, 단위 등이 구조화된 형태로 제공됩니다.' },
    { icon: BarChart3, title: '비용 정보 및 견적 내역', desc: 'SnapPrice AI가 계산한 부위별 공사비, 단가, 공수 정보가 포함됩니다. 이를 통해 설계 변경 시 실시간으로 비용 변화를 확인할 수 있습니다.' },
    { icon: Database, title: '건축 요소 및 속성 정보', desc: 'BIM 모델에서 추출한 건축 요소(벽, 바닥, 천장 등)의 속성과 기하학적 정보를 포함합니다. 이는 설계 의도를 정확히 전달하는 핵심 데이터입니다.' },
    { icon: Shield, title: '디지털 서명 및 무결성 정보', desc: 'EDX 포맷과 유사하게 XML 디지털 서명으로 파일 무결성을 검증하고, 객체별 암호화로 무단 변경을 방지하는 보안 기능을 제공합니다.' },
];

const SECURITY_LEVELS = [
    { level: 1, data: '컨셉 설계 및 초기 스케치', feature: '아이디어 수준의 초기 데이터 (정합성: 초기)', color: '#3b82f6' },
    { level: 2, data: '컨셉 + 2D 평면/입단면 정보', feature: '2D 정보 연계 시작 (정합성: 기초)', color: '#06b6d4' },
    { level: 3, data: '컨셉 + 2D + 3D 모델링 + 자재/시공 정보', feature: '3D 구현 시작 및 디테일 추가 (정합성: 발전)', color: '#22c55e' },
    { level: 4, data: '위 모든 데이터가 학습된 ML 데이터셋', feature: '기업 노하우 집약체 (정합성: 지식화)', color: '#f59e0b' },
    { level: 5, data: '학습 완료된 ML 모델', feature: '실제 적용 가능한 AI 모델 (정합성: 자동화)', color: '#ef4444' },
];

const SECURITY_CARDS = [
    { icon: Lock, title: '보안 적용 방안', items: ['등급별 차등화된 접근 권한 제어', '등급에 따른 데이터 암호화 수준 차별화', '디지털 서명으로 데이터 무결성 검증', '객체별 암호화로 무단 변경 방지'] },
    { icon: Fingerprint, title: '설계 데이터 무결성 보장', items: ['CodexHub 차원의 디지털 서명 적용', 'EDX 포맷 기반 XML 서명으로 불변성 검증', '데이터 변경 이력 추적 및 감사', '블록체인 기술 연계 데이터 보호'] },
    { icon: Key, title: 'CXD 접근 제어 정책', items: ['ML 데이터 완성도에 따른 접근 권한 차등화', 'Level 4-5 데이터에 대한 강화된 인증 절차', '설계 민감정보 마스킹 및 암호화 적용', '기업별 커스터마이징 가능한 권한 관리'] },
];

const EFFICIENCY_STATS = [
    { value: '45-60분', label: '기존 방식\n도면 1장당 소요 시간', color: '#ef4444' },
    { value: '<1분', label: 'AI 추정 방식\n도면 1장당 소요 시간', color: '#22c55e' },
    { value: '99%', label: 'AI 도면 해석\n정확도', color: '#3b82f6' },
];

/* ═══════════════════ PAGE ═══════════════════════ */
export default function SnapCodexPage() {
    return (
        <ThemeProvider>
            <SnapCodexContent />
        </ThemeProvider>
    );
}

function SnapCodexContent() {
    const { theme, toggleTheme } = useTheme();
    const d = theme === 'dark';
    return (
        <main className={`min-h-screen pt-16 transition-colors duration-300 ${d ? 'bg-gradient-to-b from-gray-950 via-[#0a0e1a] to-gray-950 text-white' : 'bg-gradient-to-b from-slate-50 via-white to-slate-50 text-gray-900'}`}>


            {/* Hero */}
            <section className="pt-32 pb-16 relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[120px] ${d ? 'bg-blue-600/10' : 'bg-blue-400/15'}`} />
                </div>
                <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 mb-8 border ${d ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}
                    >
                        <Cpu className={`w-4 h-4 ${d ? 'text-blue-400' : 'text-blue-600'}`} />
                        <span className={`text-sm ${d ? 'text-blue-300' : 'text-blue-700'}`}>Patented AI Technology</span>
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className={`text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent ${d ? 'bg-gradient-to-r from-white via-blue-100 to-blue-300' : 'bg-gradient-to-r from-gray-900 via-blue-800 to-blue-600'}`}
                    >
                        SnapCodex 시스템 아키텍처
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className={`text-lg max-w-3xl mx-auto ${d ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                        BIM 설계 모델에서 AI 분석, 비용 산출, CXD 생성까지 —<br />
                        <span className={`font-semibold ${d ? 'text-blue-400' : 'text-blue-600'}`}>설계 자산의 디지털 전환</span>을 자동화하는 통합 파이프라인
                    </motion.p>
                </div>
            </section>

            {/* ────── 1. 시스템 아키텍처와 데이터 흐름 ────── */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">시스템 아키텍처와 데이터 흐름</h2>

                    {/* Pipeline Flow */}
                    <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0 mb-16">
                        {PIPELINE_STEPS.map((step, i) => (
                            <div key={step.label} className="flex items-center">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.15 }}
                                    className={`flex flex-col items-center gap-3 p-5 rounded-2xl border min-w-[160px] ${d ? 'border-white/10 bg-white/[0.03]' : 'border-gray-200 bg-white shadow-sm'}`}
                                >
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${step.color}20` }}>
                                        <step.icon className="w-6 h-6" style={{ color: step.color }} />
                                    </div>
                                    <span className="font-bold text-sm" style={{ color: step.color }}>{step.label}</span>
                                    <span className={`text-[10px] text-center leading-tight max-w-[140px] ${d ? 'text-gray-400' : 'text-gray-500'}`}>{step.desc}</span>
                                </motion.div>
                                {i < PIPELINE_STEPS.length - 1 && (
                                    <div className="hidden md:flex items-center px-3">
                                        <motion.div
                                            animate={{ x: [0, 5, 0] }}
                                            transition={{ duration: 1.5, repeat: Infinity }}
                                        >
                                            <ArrowRight className={`w-5 h-5 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
                                        </motion.div>
                                        <span className={`text-[10px] ml-1 ${d ? 'text-gray-500' : 'text-gray-500 font-medium'}`}>
                                            {i === 0 ? 'EDX 포맷' : i === 1 ? '구조화 데이터' : '견적 정보'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Detail Cards */}
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className={`rounded-2xl p-6 border ${d ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <h3 className="font-bold text-blue-400 mb-4 flex items-center gap-2"><Brain className="w-5 h-5" /> CodexHub 세부 기능</h3>
                            <ul className="space-y-2.5">
                                {CODEXHUB_FEATURES.map(f => (
                                    <li key={f} className={`text-sm flex items-start gap-2 ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                        <CheckCircle className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className={`rounded-2xl p-6 border ${d ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <h3 className="font-bold text-purple-400 mb-4 flex items-center gap-2"><Calculator className="w-5 h-5" /> SnapPrice 세부 기능</h3>
                            <ul className="space-y-2.5">
                                {SNAPPRICE_FEATURES.map(f => (
                                    <li key={f} className={`text-sm flex items-start gap-2 ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                        <CheckCircle className="w-3.5 h-3.5 text-purple-400 flex-shrink-0 mt-0.5" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className={`rounded-2xl p-6 border ${d ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <h3 className="font-bold text-amber-400 mb-4 flex items-center gap-2"><FileJson className="w-5 h-5" /> CXD 구성요소</h3>
                            <ul className="space-y-2.5">
                                {CXD_COMPONENTS.map(f => (
                                    <li key={f} className={`text-sm flex items-start gap-2 ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                        <CheckCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* ────── 2. CXD 개념과 구성요소 ────── */}
            <section className={`py-16 ${d ? 'bg-gradient-to-b from-blue-950/20 to-transparent' : ''}`}>
                <div className="max-w-7xl mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className={`rounded-2xl p-6 mb-12 border ${d ? 'bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}
                    >
                        <h2 className={`text-xl font-bold mb-3 flex items-center gap-2 ${d ? 'text-blue-300' : 'text-blue-700'}`}>
                            <FileJson className="w-5 h-5" /> CXD (Completed/Certified eXchange Data)란?
                        </h2>
                        <p className={`text-sm leading-relaxed ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                            CXD는 ML 학습을 거쳐 상품화된 구조화 JSON 데이터로, 건축 자재 목록(BOM)과 공사비 정보 등을 포함하는 디지털 자산입니다.
                            설계 및 견적 정보를 한데 묶어 디지털 트윈 역할을 수행하며, AI와 로봇 시스템의 고차원 명령어로 활용될 수 있습니다.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* Left: CXD 주요 구성요소 */}
                        <div>
                            <h3 className={`text-lg font-bold mb-6 ${d ? 'text-white' : 'text-gray-900'}`}>CXD 주요 구성요소</h3>
                            <div className="space-y-4">
                                {CXD_DETAIL_ITEMS.map((item, i) => (
                                    <motion.div
                                        key={item.title}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.1 }}
                                        className={`rounded-xl p-4 border transition-all ${d ? 'bg-white/[0.03] border-white/10 hover:border-blue-500/30' : 'bg-white border-gray-200 shadow-sm hover:border-blue-400'}`}
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <item.icon className={`w-4 h-4 ${d ? 'text-blue-400' : 'text-blue-600'}`} />
                                            <h4 className={`font-semibold text-sm ${d ? 'text-white' : 'text-gray-900'}`}>{item.title}</h4>
                                        </div>
                                        <p className={`text-xs leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>{item.desc}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Right: CXD JSON 구조 예시 */}
                        <div>
                            <h3 className={`text-lg font-bold mb-6 ${d ? 'text-white' : 'text-gray-900'}`}>CXD JSON 구조 예시</h3>
                            <div className={`rounded-xl p-5 font-mono text-xs overflow-x-auto border ${d ? 'bg-gray-900/80 border-white/10' : 'bg-gray-900 border-gray-300'}`}>
                                <pre className="text-green-400 whitespace-pre">{CXD_JSON}</pre>
                            </div>

                            {/* 전략적 가치 */}
                            <div className={`mt-6 rounded-xl p-5 border ${d ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
                                <h4 className={`font-bold mb-2 flex items-center gap-2 ${d ? 'text-amber-300' : 'text-amber-700'}`}>
                                    <Globe className="w-4 h-4" /> CXD의 전략적 가치
                                </h4>
                                <p className={`text-xs leading-relaxed ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                    CXD는 단순한 데이터 구조를 넘어 <span className={`font-semibold ${d ? 'text-amber-300' : 'text-amber-700'}`}>설계-시공-견적</span>을 연결하는 디지털 자산입니다.
                                    BIM 모델과 AI 기술을 결합하여 건설 프로세스 전반의 자동화를 지원하며, 향후 로봇 건설 시스템의 명령어 역할을 함으로써 건설업의 디지털 전환을 가속화합니다.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ────── 3. ML 데이터 보안 등급 체계 ────── */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center">ML 데이터 보안 등급 체계</h2>

                    {/* Importance banner */}
                    <div className={`rounded-2xl p-5 mb-10 max-w-4xl mx-auto border ${d ? 'bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
                        <h3 className={`font-bold mb-2 ${d ? 'text-blue-300' : 'text-blue-700'}`}>설계 데이터 보안의 중요성</h3>
                        <p className={`text-xs leading-relaxed ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                            설계 데이터는 그 완결성과 상세도에 따라 보안 등급을 차등 적용해야 합니다.
                            SnapCodex 시스템에서는 데이터의 민감도와 완성도에 따라 5단계 보안 등급 체계를 도입하여 접근 권한 제어, 암호화 적용 등 차별화된 보안 정책을 적용합니다.
                        </p>
                    </div>

                    {/* Security Level Table */}
                    <div className="max-w-5xl mx-auto mb-12">
                        <div className={`rounded-2xl overflow-hidden border ${d ? 'bg-white/[0.02] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <div className={`grid grid-cols-[80px_1fr_1fr] border-b ${d ? 'border-white/10' : 'border-gray-200'}`}>
                                <div className={`p-4 text-xs font-semibold ${d ? 'text-blue-400' : 'text-blue-600'}`}>보안등급</div>
                                <div className={`p-4 text-xs font-semibold ${d ? 'text-blue-400' : 'text-blue-600'}`}>데이터 설명</div>
                                <div className={`p-4 text-xs font-semibold ${d ? 'text-blue-400' : 'text-blue-600'}`}>주요 특징 (정합성 수준)</div>
                            </div>
                            {SECURITY_LEVELS.map((sl) => (
                                <div key={sl.level} className={`grid grid-cols-[80px_1fr_1fr] border-b transition-colors ${d ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-100 hover:bg-gray-50'}`}>
                                    <div className="p-4 flex items-center gap-2">
                                        <span
                                            className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white"
                                            style={{ backgroundColor: sl.color }}
                                        >
                                            {sl.level}
                                        </span>
                                        <span className={`text-xs font-bold ${d ? 'text-white' : 'text-gray-900'}`}>Level {sl.level}</span>
                                    </div>
                                    <div className={`p-4 text-xs ${d ? 'text-gray-300' : 'text-gray-600'}`}>{sl.data}</div>
                                    <div className={`p-4 text-xs ${d ? 'text-gray-400' : 'text-gray-500'}`}>{sl.feature}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Security Cards */}
                    <div className="grid md:grid-cols-3 gap-5">
                        {SECURITY_CARDS.map((card) => (
                            <div key={card.title} className={`rounded-2xl p-5 border ${d ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                <div className="flex items-center gap-2 mb-4">
                                    <card.icon className={`w-5 h-5 ${d ? 'text-blue-400' : 'text-blue-600'}`} />
                                    <h4 className={`font-bold text-sm ${d ? 'text-white' : 'text-gray-900'}`}>{card.title}</h4>
                                </div>
                                <ul className="space-y-2">
                                    {card.items.map(item => (
                                        <li key={item} className={`text-xs flex items-start gap-2 ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                            <span className={`mt-0.5 ${d ? 'text-blue-400' : 'text-blue-600'}`}>•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ────── 4. 디자인 데이터 저작권 보호 ────── */}
            <section className={`py-16 ${d ? 'bg-gradient-to-b from-purple-950/10 to-transparent' : ''}`}>
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">디자인 데이터 저작권 보호의 필요성</h2>

                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* Left */}
                        <div className="space-y-6">
                            <div className={`rounded-2xl p-6 border ${d ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                <h3 className={`font-bold mb-3 ${d ? 'text-purple-300' : 'text-purple-600'}`}>디자인 정보의 법적 보호</h3>
                                <p className={`text-xs leading-relaxed mb-4 ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                    CXD를 포함한 디자인 정보는 창작성이 인정되는 저작물로서, 명확한 저작권 보호가 필요합니다.
                                    건축 저작물은 「저작권법」에서 보호되는 예술 저작물이며, 도면, 평면도, BIM 모델 등도 저작권 보호 대상입니다.
                                </p>

                                <h4 className={`font-semibold text-sm mb-2 flex items-center gap-2 ${d ? 'text-white' : 'text-gray-900'}`}>
                                    <Scale className="w-4 h-4 text-purple-400" /> 법적 근거
                                </h4>
                                <div className="space-y-3 mb-4">
                                    <div className={`p-3 rounded-lg border ${d ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
 <p className="text-[10px] text-gray-500 mb-1"><Landmark className="w-4 h-4 inline-block" />저작권법 보호 대상</p>
                                        <p className={`text-xs ${d ? 'text-gray-300' : 'text-gray-600'}`}>건축물과 설계도서는 저작권법상 저작물로 규정되어 있으며, 건축사의 창작적 설계 행위는 저작자의 권리로 인정됩니다.</p>
                                    </div>
                                    <div className={`p-3 rounded-lg border ${d ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
 <p className="text-[10px] text-gray-500 mb-1"><Globe className="w-4 h-4 inline-block" />국제적 판례</p>
                                        <p className={`text-xs ${d ? 'text-gray-300' : 'text-gray-600'}`}>"건축 설계 도면, 청사진, BIM 모델 등도 저작권 보호를 받는다"는 캐나다 등 국제적 판례가 있습니다.</p>
                                    </div>
                                    <div className={`p-3 rounded-lg border ${d ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
 <p className="text-[10px] text-gray-500 mb-1"><Lock className="w-4 h-4 inline-block" />디지털 자산 보호</p>
                                        <p className={`text-xs ${d ? 'text-gray-300' : 'text-gray-600'}`}>SnapCodex에서 생성·공유되는 CXD 데이터도 설계자의 지적 창작물로 보고 보호해야 합니다.</p>
                                    </div>
                                </div>

                                <div className={`p-4 rounded-xl italic border ${d ? 'bg-purple-500/5 border-purple-500/10' : 'bg-purple-50 border-purple-200'}`}>
                                    <p className={`text-xs leading-relaxed ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                        " 건축 설계 도면, 청사진, BIM 모델 등도 저작권 보호를 받으며, 설계 방법론과 같은 설계 아이디어까지 보호되는 범위가 확장되고 있습니다. "
                                    </p>
                                    <p className="text-[10px] text-gray-500 mt-2 text-right">- 캐나다 저작권법 관련 판례 (marks-clerk.com)</p>
                                </div>
                            </div>
                        </div>

                        {/* Right */}
                        <div className="space-y-6">
                            <div className={`rounded-2xl p-6 border ${d ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                                    <Eye className="w-5 h-5" /> 보호 부재 시 리스크
                                </h3>
                                {[
                                    { title: '무단 복제 및 도용', desc: '저작권 보호가 명확히 보장되지 않으면, 설계 데이터 무단 복제나 무단 활용 시 디지털 자산 가치가 훼손될 위험이 있습니다.' },
                                    { title: '경제적 가치 상실', desc: '지적재산권이 보호되지 않으면 설계자는 자신의 창작물에 대한 경제적 보상을 받지 못하고, 결과적으로 양질의 설계 개발 동기가 저하됩니다.' },
                                    { title: '시장 실패', desc: '저작권이 불명확하면 설계 정보의 거래 시장이 형성되기 어려워 디지털 설계 생태계의 성장이 저해됩니다.' },
                                ].map(risk => (
                                    <div key={risk.title} className="mb-4 last:mb-0">
                                        <h4 className={`text-sm font-semibold mb-1 ${d ? 'text-white' : 'text-gray-900'}`}>{risk.title}</h4>
                                        <p className={`text-xs leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>{risk.desc}</p>
                                    </div>
                                ))}
                            </div>

                            <div className={`rounded-2xl p-6 border ${d ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                                <h3 className="font-bold text-emerald-400 mb-4 flex items-center gap-2">
                                    <Shield className="w-5 h-5" /> 저작권 보호의 이점
                                </h3>
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { icon: TrendingUp, title: '혁신 촉진', desc: '설계자의 창작 의욕 증진과 혁신적 설계 기술 개발 장려' },
                                        { icon: BarChart3, title: '거래 활성화', desc: '명확한 저작권으로 디지털 설계 정보 거래 촉진' },
                                        { icon: Layers, title: '가치 창출', desc: '설계자·개발자 간 마이크로페이먼트 생태계 구축' },
                                    ].map(b => (
                                        <div key={b.title} className={`text-center p-3 rounded-xl border ${d ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50 border-emerald-200'}`}>
                                            <b.icon className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
                                            <div className={`text-xs font-bold mb-1 ${d ? 'text-white' : 'text-gray-900'}`}>{b.title}</div>
                                            <div className={`text-[10px] leading-tight ${d ? 'text-gray-400' : 'text-gray-500'}`}>{b.desc}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ────── 5. 기대효과: BIM 기반 실시간 예산·설계 최적화 ────── */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">기대효과: BIM 기반 실시간 예산·설계 최적화</h2>

                    <div className="grid lg:grid-cols-2 gap-8 mb-12">
                        {/* 효율성 */}
                        <div className={`rounded-2xl p-6 border ${d ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <h3 className="font-bold text-blue-400 mb-4 flex items-center gap-2">
                                <BarChart3 className="w-5 h-5" /> 효율성 및 시간 절감 효과
                            </h3>
                            <div className="space-y-4 mb-6">
                                <div className={`p-4 rounded-xl border ${d ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                    <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${d ? 'text-white' : 'text-gray-900'}`}>
                                        <Clock className="w-4 h-4 text-blue-400" /> 비용 산출 시간 획기적 단축
                                    </h4>
                                    <p className={`text-xs leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                                        전통적으로 도면 한 페이지의 비용 산출에는 평균 45~60분이 소요되지만, AI 추정 소프트웨어는 이를 1분 이내로 단축할 수 있습니다.
                                        SnapCodex/CXD는 BIM 기반 실시간 견적 산출로 설계 변경 시마다 수작업으로 이루어지던 견적 작업을 자동화합니다.
                                    </p>
                                </div>
                                <div className={`p-4 rounded-xl border ${d ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                    <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${d ? 'text-white' : 'text-gray-900'}`}>
                                        <Zap className="w-4 h-4 text-amber-400" /> 설계-예산 반복 주기 단축
                                    </h4>
                                    <p className={`text-xs leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                                        설계 변경 시마다 실시간으로 비용을 업데이트함으로써 설계 수정·예산 업데이트의 속도를 높이고,
                                        설계 단계부터 예산 편차를 실시간 감지할 수 있습니다.
                                    </p>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-3">
                                {EFFICIENCY_STATS.map(s => (
                                    <div key={s.value} className={`text-center p-4 rounded-xl border ${d ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                        <div className="text-2xl font-bold mb-1" style={{ color: s.color }}>{s.value}</div>
                                        <div className={`text-[10px] whitespace-pre-line leading-tight ${d ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
                                    </div>
                                ))}
                            </div>

                            <div className={`mt-4 p-3 rounded-xl italic border ${d ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50 border-blue-200'}`}>
                                <p className={`text-[10px] ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                                    "AI를 활용한 예측적 비용 추정은 과거 데이터를 활용해 예측 정확도를 높이고, 인력을 절약하며 경쟁력을 키울 수 있도록 돕는다."
                                    <span className="not-italic text-gray-500 ml-1">- Keymakr 분석 보고서</span>
                                </p>
                            </div>
                        </div>

                        {/* 표준화 */}
                        <div className={`rounded-2xl p-6 border ${d ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                            <h3 className="font-bold text-emerald-400 mb-4 flex items-center gap-2">
                                <Bot className="w-5 h-5" /> 표준화 및 협업 향상 효과
                            </h3>
                            <div className="space-y-4">
                                <div className={`p-4 rounded-xl border ${d ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                    <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${d ? 'text-white' : 'text-gray-900'}`}>
                                        <ArrowRight className="w-4 h-4 text-emerald-400" /> 일관된 데이터 교환 체계
                                    </h4>
                                    <p className={`text-xs leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                                        CXD 중심의 데이터 교환 체계는 협업을 용이하게 합니다. 표준화된 CXD 형식은 설계자, 건설사, 발주처 간에 일관된 정보 전달을 보장합니다.
                                    </p>
                                </div>
                                <div className={`p-4 rounded-xl border ${d ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                    <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${d ? 'text-white' : 'text-gray-900'}`}>
                                        <Server className="w-4 h-4 text-cyan-400" /> 공정 스케줄링 및 자동견적 연계
                                    </h4>
                                    <p className={`text-xs leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                                        표준화된 CXD는 공정 스케줄링과 자동견적에도 활용될 수 있습니다. CXD에 시공 단계 및 일정 정보를 포함시키면,
                                        자동화된 시뮬레이션이나 로봇 공정계획 툴에서 직접 활용할 수 있어 프로젝트 일정 최적화가 가능합니다.
                                    </p>
                                </div>
                                <div className={`p-4 rounded-xl border ${d ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                                    <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${d ? 'text-white' : 'text-gray-900'}`}>
                                        <Cpu className="w-4 h-4 text-purple-400" /> AI 및 로봇 건설 연계 효과
                                    </h4>
                                    <p className={`text-xs leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                                        CostOS, ALICE, Autodesk BIM 360과 같은 AI 도구와 연계하여 과거 사례와 실시간 시장 데이터를 종합적으로 분석해
                                        예산·자원 계획을 개선할 수 있습니다.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══ Adoption Packages ═══ */}
            <section className={`py-16 border-t ${d ? 'border-white/5' : 'border-gray-200'}`} id="packages">
                <div className="max-w-6xl mx-auto px-6">
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
                        <h2 className="text-3xl font-bold mb-4">SnapCodex 도입 패키지</h2>
                        <p className={`text-sm max-w-xl mx-auto ${d ? 'text-gray-400' : 'text-gray-500'}`}>기업 규모와 필요에 맞는 패키지를 선택하세요. 모든 패키지에 초기 컨설팅 포함.</p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Starter */}
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                            className={`rounded-2xl p-6 border transition-all ${d ? 'border-white/10 bg-white/[0.02] hover:border-blue-500/30' : 'border-gray-200 bg-white shadow-sm hover:border-blue-400'}`}>
                            <div className="text-blue-400 text-xs font-bold mb-2">STARTER</div>
                            <h3 className="text-xl font-bold mb-1">스타터</h3>
                            <p className="text-xs text-gray-500 mb-4">NAS 자산 진단 + 샘플 구조화</p>
                            <div className="mb-4">
                                <span className="text-3xl font-black">1,200</span>
                                <span className={`text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>만원~</span>
                                <p className="text-[10px] text-gray-500 mt-1">⏱️ 구축 4~6주 | 월 유지보수 30만원</p>
                            </div>
                            <div className="space-y-2 mb-6">
                                {[
                                    'NAS 설계 자산 규모 진단·분류체계 설계',
                                    '샘플 1 프로젝트 관계형 DB 구조화',
                                    '메타데이터 스키마 설계 + 기본 검색',
                                    '클라우드 기반 RAG 챗봇 (기본)',
                                    '도입 컨설팅 1회 + 결과 보고서',
                                ].map(f => (
                                    <div key={f} className={`flex items-start gap-2 text-xs ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                        <CheckCircle className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" /> {f}
                                    </div>
                                ))}
                            </div>
                            <div className={`p-3 rounded-lg mb-4 border ${d ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50 border-blue-200'}`}>
                                <p className={`text-[10px] ${d ? 'text-blue-300' : 'text-blue-700'}`}><strong>산출물:</strong> 자산 진단 보고서, DB 스키마, 샘플 구조화 데이터, 검색 데모</p>
                            </div>
                            <Link href="/" className={`block text-center py-2.5 rounded-xl text-sm font-medium transition-all border ${d ? 'bg-white/5 hover:bg-white/10 border-white/10' : 'bg-gray-100 hover:bg-gray-200 border-gray-200 text-gray-700'}`}>
                                시작하기
                            </Link>
                        </motion.div>

                        {/* Pro */}
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                            className={`rounded-2xl p-6 relative border-2 ${d ? 'border-purple-500/40 bg-gradient-to-b from-purple-900/10 to-transparent' : 'border-purple-400 bg-gradient-to-b from-purple-50 to-white shadow-lg'}`}>
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 px-4 py-1 rounded-full text-[10px] font-bold text-white">RECOMMENDED</div>
                            <div className="text-purple-400 text-xs font-bold mb-2">PRO</div>
                            <h3 className="text-xl font-bold mb-1">프로</h3>
                            <p className="text-xs text-gray-500 mb-4">풀 파이프라인 구축 + 대시보드</p>
                            <div className="mb-4">
                                <span className="text-3xl font-black">2,500</span>
                                <span className={`text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>만원~</span>
                                <p className="text-[10px] text-gray-500 mt-1">⏱️ 구축 6~10주 | 월 유지보수 50만원</p>
                            </div>
                            <div className="space-y-2 mb-6">
                                {[
                                    'CodexHub + SnapPrice + CXD 파이프라인',
                                    '전체 프로젝트 DB 구조화 (도면/내역/스펙)',
                                    '교차 검색 체계 + 메타데이터 통합',
                                    '관리 대시보드 + 권한별 접근제어',
                                    '하이브리드 RAG 챗봇 (CAD/법규 지원)',
                                    '분기별 데이터 업데이트 1년',
                                ].map(f => (
                                    <div key={f} className={`flex items-start gap-2 text-xs ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                        <CheckCircle className="w-3.5 h-3.5 text-purple-400 flex-shrink-0 mt-0.5" /> {f}
                                    </div>
                                ))}
                            </div>
                            <div className={`p-3 rounded-lg mb-4 border ${d ? 'bg-purple-500/5 border-purple-500/10' : 'bg-purple-50 border-purple-200'}`}>
                                <p className={`text-[10px] ${d ? 'text-purple-300' : 'text-purple-700'}`}><strong>산출물:</strong> 관계형 DB, 검색 대시보드, RAG 챗봇, CXD 자산, 운영 가이드</p>
                            </div>
                            <Link href="/" className="block text-center py-2.5 bg-purple-600 hover:bg-purple-700 rounded-xl text-sm font-semibold transition-all">
                                상담 신청
                            </Link>
                        </motion.div>

                        {/* Enterprise */}
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
                            className={`rounded-2xl p-6 border ${d ? 'border-amber-500/20 bg-gradient-to-b from-amber-900/5 to-transparent' : 'border-amber-300 bg-gradient-to-b from-amber-50 to-white shadow-sm'}`}>
                            <div className="text-amber-400 text-xs font-bold mb-2">ENTERPRISE</div>
                            <h3 className="text-xl font-bold mb-1">엔터프라이즈</h3>
                            <p className="text-xs text-gray-500 mb-4">온프레미스 + 커스텀 DSLM + 교육</p>
                            <div className="mb-4">
                                <span className="text-3xl font-black">별도 협의</span>
                                <p className="text-[10px] text-gray-500 mt-1">⏱️ 구축 8~16주 | 전담 운영팀</p>
                            </div>
                            <div className="space-y-2 mb-6">
                                {[
                                    'Pro 전체 포함 + 온프레미스 서버 구축',
                                    '커스텀 DSLM Fine-tuning (도메인 특화)',
                                    'SnapGRID 9 에이전트 배포',
                                    '에어갭 지원 (폐쇄망 100%)',
                                    'CXD→건설로봇(Physical AI) 연계',
                                    '전담 운영/모니터링 + 사내 교육',
                                    'SLA 보장 + 연간 업데이트',
                                ].map(f => (
                                    <div key={f} className={`flex items-start gap-2 text-xs ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                                        <CheckCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" /> {f}
                                    </div>
                                ))}
                            </div>
                            <div className={`p-3 rounded-lg mb-4 border ${d ? 'bg-amber-500/5 border-amber-500/10' : 'bg-amber-50 border-amber-200'}`}>
                                <p className={`text-[10px] ${d ? 'text-amber-300' : 'text-amber-700'}`}><strong>산출물:</strong> 전용 서버, DSLM 모델, 대시보드, 교육 자료, 운영 SLA 문서</p>
                            </div>
                            <Link href="/" className="block text-center py-2.5 bg-amber-600 hover:bg-amber-700 rounded-xl text-sm font-semibold transition-all">
                                별도 상담
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className={`py-16 border-t ${d ? 'border-white/5' : 'border-gray-200'}`}>
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="text-2xl font-bold mb-4">SnapCodex 도입 상담</h2>
                    <p className={`text-sm mb-8 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                        BIM 기반 설계 자산의 디지털 전환과 AI 비용 산출 자동화를 시작하세요.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            href="/"
                            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 text-white"
                        >
                            메인 페이지로
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <a
                            href="https://www.desinsight.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all border ${d ? 'bg-white/5 hover:bg-white/10 border-white/10' : 'bg-gray-100 hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                        >
                            SnapCodex 공식 사이트
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className={`py-8 border-t ${d ? 'border-white/5' : 'border-gray-200'}`}>
                <div className={`max-w-7xl mx-auto px-6 text-center text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                    © 2026 Desinsight. All rights reserved. | Powered by SnapCodex Patented Technology
                </div>
            </footer>
        </main>
    );
}
